import { colors } from '@lmc/common/constants'
import { Button, Icon as UiKitIcon } from '@lmc/ui-kit'
import { get } from 'lodash'
import React, { Suspense } from 'react'
import Field from '../../fields'
import { FamilyStatusBadge, Icon } from '../../generic'

interface ImportantContactFormProps {
  contactsCount: number
  fields: Record<string, any>
  formId: string
  index: number
  onAdd: () => void
  onDelete: () => void
  style?: React.CSSProperties
  family?: boolean
}

export default class ImportantContactForm extends React.Component<ImportantContactFormProps> {
  getKey = (key: string) => `importantContacts[${this.props.index}].${key}`

  canAddNewContact = () => {
    const { contactsCount, fields, index } = this.props
    const isLastContact = index + 1 === contactsCount

    return (
      !!get(fields, `importantContacts[${index}]`) &&
      (contactsCount === 1 || isLastContact)
    )
  }

  renderFamilyPortalNote = (name: string) => {
    return `This decision should always be in ${name}'s best interests if they cannot make it themselves. If no is selected, you will not be able to send an invite.`
  }

  renderFamilyPortalBadge = (importantContact: Record<string, any>) => {
    if (!importantContact) return null

    return (
      <FamilyStatusBadge
        hideWarning
        contact={importantContact}
        residentName={undefined}
      />
    )
  }

  renderFields = () => {
    const { fields, formId, index, family } = this.props

    return (
      <div>
        <div style={styles.formField}>
          <Suspense fallback={<div>Loading...</div>}>
            <Field
              formId={formId}
              keys={this.getKey('type')}
              label="Important contact type"
              layout="no-label"
              options={{
                off: {
                  color: colors.purple,
                  label: 'Personal',
                  value: 'Personal'
                },
                on: {
                  color: colors.darkblue,
                  label: 'Professional',
                  value: 'Professional'
                }
              }}
              type="switch"
              values={fields}
            />
          </Suspense>
        </div>
        <div style={styles.formField}>
          <Suspense fallback={<div>Loading...</div>}>
            <Field
              isNestedField
              keys={[this.getKey('name.first'), this.getKey('name.last')]}
              label="Name"
              errorHelpText="Important Contact"
              formId={formId}
              required
              type="multiText"
              values={fields}
            />
          </Suspense>
        </div>
        <div style={styles.formField}>
          <Suspense fallback={<div>Loading...</div>}>
            <Field
              isNestedField
              keys={this.getKey('relationship')}
              label="Relationship"
              formId={formId}
              type="text"
              values={fields}
            />
          </Suspense>
        </div>
        <div style={styles.formField}>
          <Suspense fallback={<div>Loading...</div>}>
            <Field
              inputType="email"
              isNestedField
              errorHelpText="Important Contact"
              keys={this.getKey('email')}
              label="Email"
              formId={formId}
              type="text"
              validationTypes="email"
              values={fields}
            />
          </Suspense>
        </div>
        <div style={styles.formField}>
          <Suspense fallback={<div>Loading...</div>}>
            <Field
              isNestedField
              keys={this.getKey('nextOfKin')}
              label="Next Of Kin"
              formId={formId}
              options={[
                { value: true, label: 'Yes' },
                { value: false, label: 'No' }
              ]}
              type="radios"
              values={fields}
            />
          </Suspense>
        </div>
        <div style={styles.formField}>
          <Suspense fallback={<div>Loading...</div>}>
            <Field
              isNestedField
              keys={this.getKey('lpa')}
              label="Lasting Power of Attorney"
              formId={formId}
              options={[
                { value: true, label: 'Yes' },
                { value: false, label: 'No' }
              ]}
              type="radios"
              values={fields}
            />
          </Suspense>
          <Suspense fallback={<div>Loading...</div>}>
            <Field
              dependsOn={{
                key: this.getKey('lpa'),
                value: true
              }}
              displayLabel={false}
              isNestedField
              keys={this.getKey('lpaTypes')}
              label="Lasting Power of Attorney types"
              errorHelpText="Important Contact"
              formId={formId}
              required
              options={[
                { value: 'Health & Wellbeing', label: 'Health & Wellbeing' },
                { value: 'Financial', label: 'Financial' }
              ]}
              type="checkbox"
              values={fields}
            />
          </Suspense>
        </div>
        <div style={styles.formField}>
          <Suspense fallback={<div>Loading...</div>}>
            <Field
              isNestedField
              keys={this.getKey('address')}
              label="Address"
              errorHelpText="Important Contact"
              formId={formId}
              type="address"
              values={fields}
            />
          </Suspense>
        </div>
        <div style={styles.formField}>
          <Suspense fallback={<div>Loading...</div>}>
            <Field
              isNestedField
              keys={this.getKey('telephone')}
              label="Telephone 1"
              errorHelpText="Important Contact"
              formId={formId}
              options={{
                phone: true,
                phoneRegionCode: 'GB'
              }}
              type="formattedText"
              values={fields}
            />
          </Suspense>
        </div>
        <div style={styles.formField}>
          <Suspense fallback={<div>Loading...</div>}>
            <Field
              isNestedField
              keys={this.getKey('telephoneSecondary')}
              label="Telephone 2"
              errorHelpText="Important Contact"
              formId={formId}
              options={{
                phone: true,
                phoneRegionCode: 'GB'
              }}
              type="formattedText"
              values={fields}
            />
          </Suspense>
        </div>
        <div style={styles.formField}>
          <Suspense fallback={<div>Loading...</div>}>
            <Field
              isNestedField
              keys={this.getKey('contactStatus')}
              label="Contact status"
              formId={formId}
              options={[
                { value: 'Priority' },
                { value: 'Secondary' },
                { value: 'Do not contact' }
              ]}
              type="radios"
              values={fields}
            />
          </Suspense>
        </div>
        <div style={styles.formField}>
          <Suspense fallback={<div>Loading...</div>}>
            <Field
              dependsOn={{
                key: this.getKey('type'),
                value: 'Professional'
              }}
              isNestedField
              keys={this.getKey('contactableTimes')}
              label="Contactable times"
              formId={formId}
              type="text"
              values={fields}
            />
          </Suspense>
        </div>
        <div style={styles.formField}>
          <Suspense fallback={<div>Loading...</div>}>
            <Field
              isNestedField
              keys={this.getKey('observations')}
              label="Observations about this person"
              formId={formId}
              style={{ resize: 'none' }}
              type="textarea"
              values={fields}
            />
          </Suspense>
        </div>
        {family && (
          <div>
            <div style={styles.familyStatusContainer}>
              <div style={styles.familyStatusLabel}>
                <Icon
                  name="family-update"
                  fill={colors.black}
                  style={styles.familyUpdateIcon}
                />
                Family App Status
              </div>
              <div style={styles.familyStatusBadgeContainer}>
                {this.renderFamilyPortalBadge(fields.importantContacts[index])}
              </div>
            </div>
            <div style={styles.formField}>
              <Suspense fallback={<div>Loading...</div>}>
                <Field
                  isNestedField
                  keys={this.getKey('familyHasAccess')}
                  label="Is this contact allowed access to the Family App?"
                  labelHelpLink={FAMILY_APP_HELP_ARTICLE}
                  formId={formId}
                  options={[
                    { value: true, label: 'Yes' },
                    { value: false, label: 'No' }
                  ]}
                  type="radios"
                  note={this.renderFamilyPortalNote(fields.name.first)}
                  values={fields}
                />
              </Suspense>
            </div>
            <div style={styles.formField}>
              <Suspense fallback={<div>Loading...</div>}>
                <Field
                  isNestedField
                  keys={this.getKey('familyComment')}
                  label="Why this decision has been made"
                  formId={formId}
                  style={{ resize: 'none' }}
                  type="textarea"
                  note="Any changes will automatically create a log."
                  values={fields}
                />
              </Suspense>
            </div>
          </div>
        )}
      </div>
    )
  }

  renderFooter = () => {
    const { onAdd, onDelete } = this.props
    return (
      <div style={styles.footer}>
        <Button
          // @ts-ignore
          className="important-contact-delete"
          onClick={onDelete}
          variant="danger"
        >
          Delete contact
        </Button>
        {this.canAddNewContact() && (
          <Button
            // @ts-ignore
            className="important-contact-add"
            onClick={onAdd}
            variant="primary"
            // @ts-ignore
            icon={<UiKitIcon name="PlusOutlined" />}
          >
            Add a new contact
          </Button>
        )}
      </div>
    )
  }

  render() {
    const { index, style } = this.props
    return (
      <div>
        <div style={style}>
          <span style={styles.title} className="important-contact-title">
            Important contact {index + 1}
          </span>
          {this.renderFields()}
          {this.renderFooter()}
        </div>
      </div>
    )
  }
}

const FAMILY_APP_HELP_ARTICLE =
  'https://support.logmycare.co.uk/en/articles/4107682-how-can-i-make-a-resident-s-relative-inactive-in-the-family-app'

const styles = {
  footer: {
    display: 'flex',
    justifyContent: 'space-between',
    paddingTop: 20
  },
  familyStatusBadge: {
    padding: '3px 9px',
    fontSize: 12
  },
  familyStatusBadgeContainer: {
    display: 'flex',
    alignItems: 'center'
  },
  familyStatusContainer: {
    paddingTop: 20,
    display: 'flex'
  },
  familyStatusLabel: {
    display: 'flex',
    alignItems: 'center',
    minWidth: 160,
    marginRight: 30
  },
  familyUpdateIcon: {
    marginRight: 5
  },
  formField: {
    paddingTop: 20
  },
  title: {
    display: 'block',
    fontSize: 18,
    fontWeight: 'bold'
  }
}
